// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { transformModuleKey } from '@atlassian/jira-servicedesk-automation-common/src/analytics';
import commonMessages from '@atlassian/jira-servicedesk-common/src/i18n';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics';
import type { BlueprintModuleKey } from '../../../model';
import messages from './messages';

export type Props = {
	isOpen: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	SelectBlueprintComponent: ComponentType<any>;
	selectedBlueprintKey: string;
	onContinue: (arg1: BlueprintModuleKey) => void;
	onClose: () => void;
};

type AddRuleProps = Props & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class AddRule extends PureComponent<AddRuleProps> {
	static defaultProps: Partial<Props> = {
		isOpen: false,
	};

	onContinue = (event: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		this.props.onContinue(this.props.selectedBlueprintKey);
		fireUIAnalytics(analyticsEvent, 'continue', {
			blueprintKey: transformModuleKey(this.props.selectedBlueprintKey),
		});
	};

	onClose = (event: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		this.props.onClose();
		fireUIAnalytics(analyticsEvent, 'cancel');
	};

	actions = [
		{ text: this.props.intl.formatMessage(commonMessages.continue), onClick: this.onContinue },
		{ text: this.props.intl.formatMessage(commonMessages.cancel), onClick: this.onClose },
	];

	render() {
		const {
			isOpen,
			onClose,
			SelectBlueprintComponent,
			intl: { formatMessage },
		} = this.props;
		return (
			<ContextualAnalyticsData
				attributes={{
					context: ANALYTIC_CONTEXTS.AUTOMATION_SETTINGS,
				}}
				sourceType={MODAL}
				sourceName="createRule"
			>
				<ModalTransition>
					{isOpen && (
						<ShortcutScope>
							<ModalDialog autoFocus width="medium" onClose={onClose}>
								<ModalHeader>
									<ModalTitle>{formatMessage(messages.newAutomationRule)}</ModalTitle>
								</ModalHeader>

								<ModalBody>
									<FireScreenAnalytics />
									<SelectBlueprintComponent />
								</ModalBody>
								<ModalFooter>
									{this.actions.map((props, index) => (
										<Button key={index} {...props} appearance={index === 0 ? 'primary' : 'subtle'}>
											{props.text}
										</Button>
									))}
								</ModalFooter>
							</ModalDialog>
						</ShortcutScope>
					)}
				</ModalTransition>
			</ContextualAnalyticsData>
		);
	}
}

export default injectIntl(AddRule);
